import React, { useCallback, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { EditTooltip } from 'screens/Residents/screens/ResidentProfileScreen/components/EditTooltip'
import { Text } from '@lmc/ui-kit/src/general'
import { colors } from '@lmc/common/constants'
import { CarePlanAdl, CarePlan } from '@lmc/ts-common'
import MultiSelectField from '../../fields/types/MultiSelectField'
import { ImageSection } from '../ListItem/components'
import { getNominative } from '../../../common/utils'
import { riskAssessmentFormConstants } from 'locale/riskAssessmentForm'
import { NAMESPACE } from 'locale'
import { loadList } from '../../../operations/Data'
import Operations from '../../../operations'

export interface CarePlanAdlExtended extends Omit<CarePlanAdl, 'icon'> {
  noFurtherSupportRequired: boolean
  value: any
  carePlan?: CarePlan
  icon: string
  iconText: string
}

interface RootState {
  data: {
    'care-plan-adls': CarePlanAdlExtended[]
  }
  carePlans?: {
    adls: CarePlanAdlExtended[]
  }
  global: {
    home: {
      preferredCarePlansNominative: string
    }
  }
}

export interface CarePlanSelectorProps {
  selectedCarePlans: CarePlanAdlExtended[] | string[]
  onChange: (carePlans: CarePlanAdlExtended[]) => void
  warningMessage?: string
  readonly?: boolean
  label?: React.ReactNode
  residentId?: string
}

export const CarePlanSelector: React.FC<CarePlanSelectorProps> = ({
  selectedCarePlans,
  onChange,
  warningMessage,
  readonly = false,
  label,
  residentId
}) => {
  const { t } = useTranslation(NAMESPACE.RISK_ASSESSMENTS_FORM)
  const dispatch = useDispatch()

  // Get data from Redux store
  const carePlans = useSelector((state: RootState) => state.carePlans)
  const carePlanAdls = useSelector(
    (state: RootState) => state.data['care-plan-adls']
  )
  const carePlanNominative = useSelector(
    (state: RootState) => state.global.home.preferredCarePlansNominative
  )

  // Fetch care-plan-adls if not already loaded
  useEffect(() => {
    if (!carePlanAdls) {
      dispatch(loadList('care-plan-adls') as any)
    }
    if (residentId) {
      dispatch(Operations.fetchResidentAdls(residentId) as any)
    }
  }, [dispatch, carePlanAdls, residentId])

  // Derive care plan options from Redux state
  const carePlansAdlsSelection = carePlans?.adls || carePlanAdls || []

  const carePlanOptions = carePlansAdlsSelection
    ?.filter(
      (item) =>
        item?.carePlan &&
        !item?.carePlan?.noFurtherSupportRequired &&
        item?.carePlan?.nextReviewDate
    )
    .map((item) => ({
      id: item.carePlan?.id,
      name: item.name,
      icon: item?.icon,
      color: item?.color
    }))

  // Normalize selectedCarePlans - if it's an array of strings (IDs), map to CarePlanAdlExtended
  const normalizedSelectedCarePlans: CarePlanAdlExtended[] =
    React.useMemo(() => {
      if (!selectedCarePlans || selectedCarePlans.length === 0) {
        return []
      }
      // Check if the first element is a string (ID) rather than an object
      if (typeof selectedCarePlans[0] === 'string') {
        const ids = selectedCarePlans as string[]
        return ids
          .map((id) => carePlanOptions.find((adl) => adl?.id === id))
          .filter((adl): adl is CarePlanAdlExtended => adl !== undefined)
      }
      return selectedCarePlans as CarePlanAdlExtended[]
    }, [selectedCarePlans, carePlansAdlsSelection])

  // Format option label for MultiSelectField
  const carePlanSelectOption = useCallback(
    ({ name, icon, color }: { name: string; icon: string; color: string }) => (
      <CarePlanOptionWrapper>
        <ImageSection popup iconColor={color} icon={icon} />
        <CarePlanName>{name}</CarePlanName>
      </CarePlanOptionWrapper>
    ),
    []
  )

  // Handle selection change
  const handleCarePlanIds = useCallback(
    (value: CarePlanAdlExtended[]) => {
      onChange(value)
    },
    [onChange]
  )

  const attachedToTranslation = t(riskAssessmentFormConstants.ATTACHED_TO, {
    carePlanNominative: getNominative(
      carePlanNominative,
      riskAssessmentFormConstants.CARE_PLANS
    )
  })

  if (readonly) {
    return (
      <>
        <CarePlanSelectorWrapper>
          <ReadonlyWrapper>
            {label ? label : <Label as="p2">{attachedToTranslation}</Label>}
            {normalizedSelectedCarePlans.length > 0 ? (
              <ReadonlyIconsContainer>
                {normalizedSelectedCarePlans.map((carePlan) => {
                  return carePlan ? (
                    <EditTooltip
                      key={carePlan?.id}
                      title={
                        <TooltipContent>
                          {' '}
                          <ImageSection
                            popup
                            iconColor={carePlan.color}
                            icon={carePlan.icon}
                          />
                          {carePlan.name}
                        </TooltipContent>
                      }
                    >
                      <TooltipWrapper>
                        <ImageSection
                          popup
                          iconColor={carePlan.color}
                          icon={carePlan.icon}
                        />
                      </TooltipWrapper>
                    </EditTooltip>
                  ) : null
                })}
              </ReadonlyIconsContainer>
            ) : (
              <Text level={1.5}>Not linked to any care plan</Text>
            )}
          </ReadonlyWrapper>
        </CarePlanSelectorWrapper>
        {warningMessage && <WarningText as="p3">{warningMessage}</WarningText>}
      </>
    )
  }

  return (
    <>
      <CarePlanSelectorWrapper>
        <MultiSelectField
          formKey={undefined}
          label={label ? label : attachedToTranslation}
          customOnChange={handleCarePlanIds}
          onChange={() => {}}
          options={carePlanOptions || []}
          formatOptionLabel={carePlanSelectOption}
          customValues={normalizedSelectedCarePlans}
          centerLabel
          errors={undefined}
          style={null}
          placeholder={''}
          values={[]}
        />
      </CarePlanSelectorWrapper>
      {warningMessage && <WarningText as="p3">{warningMessage}</WarningText>}
    </>
  )
}

const CarePlanSelectorWrapper = styled.div`
  margin-top: 10px;
  background: ${colors.white};
  border-radius: 15px;
  padding: 15px;
  display: flex;
`

const WarningText = styled(Text)`
  color: ${colors.red};
`

const CarePlanOptionWrapper = styled.div`
  align-items: center;
  display: flex;
`
CarePlanOptionWrapper.displayName = 'CarePlanOptionWrapper'

const CarePlanName = styled.span``

const ReadonlyWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const Label = styled(Text)`
  font-weight: 700;
  font-size: 16px;
`

const ReadonlyIconsContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const TooltipWrapper = styled.div`
  display: inline-flex;
`

const TooltipContent = styled.div`
  display: flex;
  align-items: center;
`
